import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, KanbanSquare, Settings, Briefcase, Phone,
  LayoutDashboard, MessageCircle, BarChart3, Wallet,
} from 'lucide-react';
import { api, type Customer, type Job } from '@/lib/api';
import { Avatar } from './Avatar';
import { cn, formatPhone } from '@/lib/utils';

interface Props {
  businessId: string;
  open: boolean;
  onClose: () => void;
}

type Item =
  | { kind: 'route'; id: string; title: string; subtitle?: string; icon: JSX.Element; to: string }
  | { kind: 'customer'; id: string; title: string; subtitle?: string; customer: Customer; to: string }
  | { kind: 'job';      id: string; title: string; subtitle?: string; job: Job;           to: string };

/**
 * ⌘K palette: jump to any CRM page, customer, or job. Search is debounced
 * and runs against the existing /customers + /jobs endpoints — no new API
 * needed.
 */
export function CommandPalette({ businessId, open, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open + focus.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Defer focus to next tick so the input is mounted.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const trimmed = query.trim();
        if (trimmed.length === 0) {
          // Without a query, show recent customers + recent jobs.
          const [cs, js] = await Promise.all([
            api.crm.listCustomers(businessId).catch(() => ({ customers: [] as Customer[] })),
            api.crm.listJobs(businessId).catch(() => ({ jobs: [] as Job[] })),
          ]);
          if (cancelled) return;
          setCustomers(cs.customers.slice(0, 5));
          setJobs(js.jobs.slice(0, 5));
        } else {
          const [cs, js] = await Promise.all([
            api.crm.listCustomers(businessId, trimmed).catch(() => ({ customers: [] as Customer[] })),
            api.crm.listJobs(businessId, { q: trimmed }).catch(() => ({ jobs: [] as Job[] })),
          ]);
          if (cancelled) return;
          setCustomers(cs.customers.slice(0, 8));
          setJobs(js.jobs.slice(0, 8));
        }
      } catch {
        /* swallow — palette is best-effort */
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [businessId, query, open]);

  const items: Item[] = useMemo(() => {
    const routes: Item[] = [
      { kind: 'route', id: 'r:overview',  title: 'Overview',  subtitle: 'Home · KPIs',           icon: <LayoutDashboard className="size-4" />, to: `/crm/${businessId}/overview`  },
      { kind: 'route', id: 'r:pipeline',  title: 'Pipeline',  subtitle: 'Jobs · kanban',         icon: <KanbanSquare    className="size-4" />, to: `/crm/${businessId}/pipeline`  },
      { kind: 'route', id: 'r:inbox',     title: 'Inbox',     subtitle: 'Contacts · messages',   icon: <MessageCircle   className="size-4" />, to: `/crm/${businessId}/inbox`      },
      { kind: 'route', id: 'r:customers', title: 'Directory', subtitle: 'Contacts · people',     icon: <Users           className="size-4" />, to: `/crm/${businessId}/customers` },
      { kind: 'route', id: 'r:reports',   title: 'Reports',   subtitle: 'Settlement · per tech', icon: <Wallet          className="size-4" />, to: `/crm/${businessId}/reports`   },
      { kind: 'route', id: 'r:analytics', title: 'Analytics', subtitle: 'Insights · charts',     icon: <BarChart3       className="size-4" />, to: `/crm/${businessId}/analytics` },
      { kind: 'route', id: 'r:settings',  title: 'Settings',  subtitle: 'Settings · agent config', icon: <Settings        className="size-4" />, to: `/crm/${businessId}/settings`  },
    ];
    const cs: Item[] = customers.map(c => ({
      kind: 'customer',
      id: `c:${c.id}`,
      title: [c.first_name, c.last_name].filter(Boolean).join(' ') || formatPhone(c.phone || '') || 'Unknown',
      subtitle: [formatPhone(c.phone || ''), c.email].filter(Boolean).join(' · '),
      customer: c,
      to: `/crm/${businessId}/customers/${c.id}`,
    }));
    const js: Item[] = jobs.map(j => ({
      kind: 'job',
      id: `j:${j.id}`,
      title: j.title,
      subtitle: [j.service_type, j.stage_key].filter(Boolean).join(' · '),
      job: j,
      to: `/crm/${businessId}/jobs/${j.id}`,
    }));
    if (query.trim()) return [...cs, ...js, ...routes];
    return [...routes, ...cs, ...js];
  }, [businessId, customers, jobs, query]);

  // Reset highlight when the items change.
  useEffect(() => { setActive(0); }, [query]);

  // Global key handlers: Esc closes, arrows navigate, Enter selects.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(v => Math.min(items.length - 1, v + 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(v => Math.max(0, v - 1)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const it = items[active];
        if (it) { navigate(it.to); onClose(); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, items, active, navigate, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start justify-center bg-black/60 px-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/95 shadow-2xl"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-zinc-800 px-4">
          <Search className="size-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search customers, jobs, or jump to a page…"
            className="h-12 w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
          />
          <kbd className="hidden rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-400 sm:inline">esc</kbd>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">No matches.</div>
          ) : items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => { navigate(it.to); onClose(); }}
              className={cn(
                'flex w-full items-center gap-3 border-b border-zinc-900 px-4 py-2.5 text-left transition',
                i === active ? 'bg-cyan-500/10' : 'hover:bg-zinc-900/60',
              )}
            >
              {it.kind === 'customer'
                ? <Avatar size={28} seed={it.customer.id} name={it.title} />
                : it.kind === 'job'
                  ? <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-violet-500/15 text-violet-300 ring-1 ring-violet-500/30"><Briefcase className="size-3.5" /></span>
                  : <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/30">{it.icon}</span>}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-zinc-100">{it.title}</div>
                {it.subtitle && <div className="truncate text-xs text-zinc-500">{it.subtitle}</div>}
              </div>
              <span className="hidden text-[10px] uppercase tracking-wide text-zinc-600 sm:block">
                {it.kind === 'route' ? 'Page' : it.kind === 'customer' ? <span className="inline-flex items-center gap-1"><Phone className="size-3" /> Customer</span> : 'Job'}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-2 text-[10px] text-zinc-500">
          <span>↑↓ navigate · ↵ select · esc close</span>
          <span><kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 py-0.5">⌘</kbd>+<kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 py-0.5">K</kbd></span>
        </div>
      </div>
    </div>
  );
}
